/**
 * musicPollAdapter - Normalizes the music generation job status response
 * into a generic `PollUpdate` consumed by GenerationContext.
 *
 * Self-registers on module load so that GenerationContext can resume
 * polling persisted music jobs after a reload.
 */

import { musicGenerationAPI } from '../musicGenerationAPI';
import type { PollAdapter, PollUpdate } from '../../types/generationJob';
import { sanitizeBackendError } from '../../utils/errorSanitizer';
import { registerPollAdapter } from './pollAdapterRegistry';

export interface MusicJobResult {
  audioUrl: string;
  audioPath: string | null;
  lyrics: string | null;
  durationSeconds: number | null;
  model: string;
  costUsd: number;
  processingTime: number;
}

export const musicPollAdapter: PollAdapter = async (
  jobId: string
): Promise<PollUpdate<MusicJobResult>> => {
  const response = await musicGenerationAPI.getJobStatus(jobId);

  if (!response.success || !response.data) {
    return {
      done: true,
      status: 'failed',
      error: {
        message: response.error ?? 'Failed to get music job status',
        sanitized: sanitizeBackendError({ error_code: response.errorCode }),
      },
    };
  }

  const data = response.data;
  const backendStatus = data.status;
  const isTerminal =
    backendStatus === 'completed' || backendStatus === 'failed' || backendStatus === 'cancelled';

  if (!isTerminal) {
    return {
      done: false,
      status: 'running',
      progress: typeof data.progress === 'number' ? data.progress : undefined,
      message: data.message,
    };
  }

  if (backendStatus === 'completed') {
    if (!data.audio_url) {
      return {
        done: true,
        status: 'failed',
        error: {
          message: 'Generation completed but no audio URL was returned',
          sanitized: sanitizeBackendError(data),
        },
      };
    }
    return {
      done: true,
      status: 'completed',
      progress: 100,
      result: {
        audioUrl: data.audio_url,
        audioPath: data.audio_path,
        lyrics: data.lyrics,
        durationSeconds: data.duration_seconds,
        model: data.model,
        costUsd: data.cost_usd,
        processingTime: data.elapsed_seconds,
      },
    };
  }

  if (backendStatus === 'cancelled') {
    return {
      done: true,
      status: 'cancelled',
      error: {
        message: data.error ?? 'Music generation was cancelled',
        sanitized: sanitizeBackendError(data),
      },
    };
  }

  return {
    done: true,
    status: 'failed',
    error: {
      message: data.error ?? 'Music generation failed',
      sanitized: sanitizeBackendError(data),
    },
  };
};

registerPollAdapter('music', musicPollAdapter);
